import React, { useState } from "react";
import { FaCheck, FaTimes, FaBuilding, FaLayerGroup, FaMapMarkedAlt } from "react-icons/fa";
import "../../styles/SubscriptionPlans.css";

const FEATURE_LABELS = {
  dashboardAccess: "Acesso ao Dashboard",
  allFunctions: "Todas as Funções",
  advancedReports: "Relatórios Avançados",
  prioritySupport: "Suporte Prioritário",
  apiIntegrations: "Integrações via API",
  automaticBackup: "Backup Automático",
  noAds: "Sem Anúncios",
  predictiveAnalytics: "IA Preditiva",
};

// Função auxiliar robusta para verificar plano atual
const checkIsCurrentPlan = (plan, sub) => {
  if (!sub) return false;
  // 1. ID direto
  if (sub.planId === plan.id) return true;
  if (sub.plan === plan.id) return true;
  // 2. Objeto/Referência
  if (sub.plan?.id === plan.id) return true;
  // 3. ID do Mercado Pago (Exceto Free)
  if (plan.id !== 'free' && sub.mercadoPagoPlanId && sub.mercadoPagoPlanId === plan.mercadoPagoPlanId) {
    return true;
  }
  return false;
};

const SubscriptionPlans = ({ plans, subscription, setSubscription, onSelectPlan, user }) => {
  const [password, setPassword] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [cancelingPlanId, setCancelingPlanId] = useState(null);

  const sortedPlans = [...plans].sort((a, b) => {
    if (checkIsCurrentPlan(a, subscription)) return -1;
    if (checkIsCurrentPlan(b, subscription)) return 1;
    return a.price - b.price;
  });

  // --- LÓGICA DE CANCELAMENTO ---
  const handleCancelSubscription = (planId) => {
    setCancelingPlanId(planId);
    setShowPasswordInput(true);
  };

  const abortCancel = () => {
    setShowPasswordInput(false);
    setCancelingPlanId(null);
    setPassword("");
  };

  const confirmCancelSubscription = async () => {
    if (!password) return alert("⚠️ Digite sua senha.");
    setIsProcessing(true);
    try {
      const userId = typeof subscription?.userId === "object" ? subscription?.userId?.id : subscription?.userId || user?.uid;
      const subscriptionId = subscription?.mercadoPagoSubscriptionId;

      if (!userId || !subscriptionId) throw new Error("Dados incompletos para cancelamento.");

      const response = await fetch("https://api.rondago.com.br/api/cancelSubscription", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId, subscription_id: subscriptionId, password }),
      });

      if (!response.ok) throw new Error(await response.text());
      const result = await response.json();
      if (!result.success) throw new Error(result.error);

      alert("✅ Assinatura cancelada com sucesso!");
      setSubscription({
        ...subscription,
        plan: { id: "free", name: "Plano Grátis", price: 0 },
        mercadoPagoSubscriptionId: null,
        status: "cancelled"
      });
      abortCancel();
    } catch (error) {
      alert(`Erro: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // --- ORÇAMENTO ---
  const handleRequestQuote = (plan) => {
    window.location.href = `mailto:suporte@rondago.com.br?subject=Orçamento - ${plan.name}&body=Solicito orçamento para ${plan.name}`;
  };

  return (
    <div className="subscription-plans-container">
      {sortedPlans.map((plan) => {
        const isCurrentPlan = checkIsCurrentPlan(plan, subscription);
        const isFreePlan = plan.id === "free" || plan.price === 0;
        
        const limits = plan.limits || {};
        const features = plan.features || limits.features || {};
        
        const maxCondos = plan.maxCondominiums ?? limits.maxCondominiums ?? 0;
        const maxTowers = plan.maxTowers ?? limits.maxTowers ?? 0;
        const maxAreas = plan.maxAreas ?? limits.maxAreas ?? 0;

        let buttonText = "Assinar";
        let buttonClass = "subscribe-button";
        if (subscription?.plan?.price !== undefined && !isCurrentPlan) {
           if (plan.price > subscription.plan.price) {
             buttonText = "Upgrade";
             buttonClass += " upgrade-button";
           } else if (plan.price < subscription.plan.price) {
             buttonText = "Downgrade";
             buttonClass += " downgrade-button";
           }
        }

        return (
          <div key={plan.id} className={`plan-card ${isCurrentPlan ? "current-plan" : ""}`}>
            {isCurrentPlan && <div className="current-badge">SEU PLANO ATUAL</div>}

            <div className="plan-header">
              <h3 className="plan-name">{plan.name}</h3>
              <div className="plan-price">
                {plan.price > 700 ? (
                  <span style={{fontSize: '1.8rem'}}>Sob Consulta</span>
                ) : (
                  <>R$ {plan.price.toFixed(2).replace('.', ',')}<span className="period">/mês</span></>
                )}
              </div>
            </div>

            <div className="plan-limits-row">
              <div className="limit-box" title="Condomínios"><FaBuilding /> <span>{maxCondos}</span></div>
              <div className="limit-box" title="Torres"><FaLayerGroup /> <span>{maxTowers}</span></div>
              <div className="limit-box" title="Áreas"><FaMapMarkedAlt /> <span>{maxAreas}</span></div>
            </div>

            <div className="plan-benefits">
              <ul>
                <li className="available"><FaCheck className="icon-check" /><span>Até {maxCondos} Condomínios</span></li>
                <li className="available"><FaCheck className="icon-check" /><span>Até {maxTowers} Torres</span></li>
                {Object.entries(FEATURE_LABELS).map(([key, label]) => {
                  const isAvailable = features[key] === true;
                  return (
                    <li key={key} className={isAvailable ? "available" : "unavailable"}>
                      {isAvailable ? <FaCheck className="icon-check" /> : <FaTimes className="icon-times" />}
                      <span>{label}</span>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="plan-actions">
              {isCurrentPlan ? (
                <>
                  <button className="current-plan-button" disabled>Plano Ativo</button>
                  {!isFreePlan && !showPasswordInput && (
                    <button className="cancel-link" onClick={() => handleCancelSubscription(plan.id)}>
                      Cancelar Assinatura
                    </button>
                  )}
                  {showPasswordInput && cancelingPlanId === plan.id && (
                    <div className="cancel-confirm-area">
                      <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Senha" className="password-input" autoFocus />
                      <div className="cancel-buttons-row">
                        <button onClick={confirmCancelSubscription} className="confirm-cancel-btn" disabled={isProcessing}>Confirmar</button>
                        <button onClick={abortCancel} className="abort-cancel-btn">Voltar</button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                plan.price > 700 ? (
                  <button className="quote-button" onClick={() => handleRequestQuote(plan)}>Orçamento</button>
                ) : (
                  !isFreePlan ? (
                    <button className={buttonClass} onClick={() => onSelectPlan(plan)}>{buttonText}</button>
                  ) : null
                )
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SubscriptionPlans;